import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Tag, X } from "lucide-react";
import {
  useActivityCategoryStore,
  type ActivityCategory,
  type CategoryInput,
} from "../store/useActivityCategoryStore";

const EMPTY: CategoryInput = {
  name: "",
  description: "",
};

const FIELD =
  "w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#2462b2]/20 focus:border-[#2462b2] transition";

export default function AdminActivityCategoriesPage() {
  const {
    categories,
    loading,
    saving,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useActivityCategoryStore();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ActivityCategory | null>(null);
  const [form, setForm] = useState<CategoryInput>(EMPTY);

  useEffect(() => {
    fetchCategories();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setModalOpen(true);
  };

  const openEdit = (c: ActivityCategory) => {
    setEditing(c);
    setForm({
      name: c.name,
      description: c.description || "",
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    const ok = editing
      ? await updateCategory(editing._id, form)
      : await createCategory(form);
    if (ok) setModalOpen(false);
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Tag className="text-[#2462b2]" size={22} />
            Activity Categories
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Categories that Business Developers pick when starting an activity.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#2462b2] text-white rounded-lg hover:bg-[#1e52a0] transition-colors text-sm font-semibold shadow-sm"
        >
          <Plus size={18} /> <span className="hidden sm:inline">New Category</span>
        </button>
      </div>

      {loading ? (
        <p className="text-slate-500">Loading...</p>
      ) : categories.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl text-slate-400">
          No categories yet. Create your first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((c) => (
            <div
              key={c._id}
              className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-2 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-800">{c.name}</span>
              </div>
              {c.description && (
                <p className="text-sm text-slate-500 line-clamp-2">
                  {c.description}
                </p>
              )}
              <div className="flex gap-2 mt-auto pt-2">
                <button
                  onClick={() => openEdit(c)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                >
                  <Pencil size={14} /> Edit
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Delete category "${c.name}"?`))
                      deleteCategory(c._id);
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-rose-500 hover:bg-rose-50 rounded-md transition-colors"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="font-bold text-slate-800">
                {editing ? "Edit Category" : "New Category"}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Name *
                </label>
                <input
                  className={FIELD}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Bidding, Research, Meeting"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Description
                </label>
                <textarea
                  className={`${FIELD} resize-none`}
                  rows={2}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-100 bg-slate-50/60">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="px-5 py-2 bg-[#2462b2] text-white rounded-lg hover:bg-[#1e52a0] transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving..." : editing ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
